import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { CheckCircle, Crown, ArrowLeft } from 'lucide-react';

const PremiumSuccess: React.FC = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [verifying, setVerifying] = useState(true);
  const [verified, setVerified] = useState(false);

  useEffect(() => {
    const verifyPayment = async () => {
      if (!sessionId) {
        setVerifying(false);
        return;
      }

      try {
        const { data, error } = await supabase.functions.invoke('verify-payment', {
          body: { sessionId },
        });

        if (error) throw error;

        setVerified(data.success);
      } catch (error) {
        console.error('Error verifying payment:', error);
      } finally {
        setVerifying(false);
      }
    };

    verifyPayment();
  }, [sessionId]);

  if (verifying) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardContent className="flex items-center justify-center p-6">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-muted-foreground">Verifying your payment...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-lg">
        <CardHeader className="text-center">
          <div className="flex items-center justify-center mb-4">
            {verified ? (
              <div className="bg-green-100 p-3 rounded-full">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
            ) : (
              <div className="bg-primary/10 p-3 rounded-full">
                <Crown className="w-8 h-8 text-primary" />
              </div>
            )}
          </div>
          <CardTitle className="text-2xl">
            {verified ? "Welcome to Premium!" : "Thank You!"}
          </CardTitle>
          <CardDescription className="text-lg">
            {verified 
              ? "Your premium access has been activated successfully."
              : "Your payment is being processed. Premium features will be available shortly."
            }
          </CardDescription>
        </CardHeader>
        
        <CardContent className="space-y-6">
          <div className="text-center space-y-4">
            <div className="bg-primary/5 p-4 rounded-lg">
              <h3 className="font-semibold text-primary mb-2">You now have access to:</h3>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• Save unlimited calculations</li>
                <li>• Export to CSV and PDF</li>
                <li>• Ad-free experience</li>
                <li>• Advanced calculator features</li>
              </ul>
            </div>
            
            <Link to="/">
              <Button className="w-full" size="lg">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Start Calculating
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PremiumSuccess;